import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

export const ATTENDANCE_HELP = {
  hub: {
    icon: "bi-clock-history",
    titleDefault: "How Attendance Hub works",
    meaningDefault:
      "The Hub is your daily clock: check in, check out, see whether you have an open session, and jump to logs or corrections.",
    whyDefault:
      "It gives a clear live status for today so you know if you are on the clock, how long you have worked, and what to do next.",
    exampleDefault:
      "Example: Check in for Office or Remote, optionally select one or more projects, then check out when you finish.",
    howDefault:
      "Use Check In / Check Out. You must check out before starting another check-in. Location may be required by your office settings. Open Daily Logs for past days, or Corrections if you need approval to change a record.",
    steps: [
      { icon: "bi-box-arrow-in-right", text: "Check in (workplace type + optional projects)." },
      { icon: "bi-hourglass-split", text: "Work your session — Hub shows you are present." },
      { icon: "bi-box-arrow-right", text: "Check out when finished (or start another session after checkout)." },
    ],
  },
  dailyLogs: {
    icon: "bi-journal-text",
    titleDefault: "How Daily Logs work",
    meaningDefault:
      "Daily Logs show the full activity stream for one day — each check-in/out, duration, projects, location, and notes.",
    whyDefault:
      "Use it to review a day in detail, fix mistakes (when allowed), or submit a correction request when your office requires approval.",
    exampleDefault:
      "Example: Session 1 pairs Check-in 09:00 with Check-out 17:00, shows 8h duration and projects “API, Mobile”.",
    howDefault:
      "Pick a date, then review sessions. Edit applies to first/last sessions when direct edit is enabled; otherwise use Submit for approval. Delete removes a whole session (or submits a deletion request). Pending requests block further changes on that event.",
    steps: [
      { icon: "bi-calendar3", text: "Select the day you want to review." },
      { icon: "bi-list-ul", text: "Read the activity stream (type, time, duration, projects)." },
      { icon: "bi-pencil-square", text: "Edit / delete directly, or submit a correction for approval." },
    ],
  },
  correctionsQueue: {
    icon: "bi-inbox",
    titleDefault: "How Correction Requests work",
    meaningDefault:
      "The corrections queue is where managers/admins review pending attendance change or deletion requests from the team.",
    whyDefault:
      "It keeps the official clock accurate while giving employees a fair way to fix mistakes without editing freely when policy forbids it.",
    exampleDefault:
      "Example: An employee asks to move a check-in from 10:15 to 09:05. You approve, and their day record updates.",
    howDefault:
      "Filter by period, office, or department. Open a request, compare current vs proposed values, then Approve or Reject with an optional comment. Exports are available for audit.",
    steps: [
      { icon: "bi-funnel", text: "Filter the queue by period and scope." },
      { icon: "bi-eye", text: "Open a request and review the proposed change." },
      { icon: "bi-check2-circle", text: "Approve or reject (optional comment to the employee)." },
    ],
  },
  submittedRequests: {
    icon: "bi-send-check",
    titleDefault: "How Submitted Requests work",
    meaningDefault:
      "This page lists correction requests you submitted — pending, approved, rejected, or withdrawn.",
    whyDefault:
      "You can track outcomes and withdraw a still-pending request if you no longer need the change.",
    exampleDefault:
      "Example: You submitted a delete request for a duplicate check-in; it shows Pending until your manager decides.",
    howDefault:
      "Use period filters to find requests. Open a row for details. Withdraw only works while status is Pending; after a decision the record stays for history.",
    steps: [
      { icon: "bi-calendar-range", text: "Filter by period to find your requests." },
      { icon: "bi-card-list", text: "Check status (Pending / Approved / Rejected / Withdrawn)." },
      { icon: "bi-x-circle", text: "Withdraw a pending request if it is no longer needed." },
    ],
  },
  reports: {
    icon: "bi-bar-chart-line",
    titleDefault: "How Attendance Reports work",
    meaningDefault:
      "Reports summarise attendance for your company — who checked in, hours worked, workplace type, and period trends.",
    whyDefault:
      "Admins use them for compliance, overtime insight, and exporting evidence for payroll or audits.",
    exampleDefault:
      "Example: Today view shows who is present; Weekly/Monthly summarises hours and missed days per employee.",
    howDefault:
      "Choose Today, Weekly, or Monthly, apply office/department/search filters, then review the grid or export Excel/CSV/PDF. Stats cards reflect the current filter scope.",
    steps: [
      { icon: "bi-calendar-week", text: "Pick period mode (Today / Weekly / Monthly)." },
      { icon: "bi-building", text: "Optional: filter by office, department, or name." },
      { icon: "bi-download", text: "Review the table or export for sharing." },
    ],
  },
  configuration: {
    icon: "bi-sliders",
    titleDefault: "How Attendance Configuration works",
    meaningDefault:
      "Configuration sets per-office rules: workday times, grace periods, flexible windows, edit/delete rights, and location requirements.",
    whyDefault:
      "Each office can run different hours and policies without changing how employees clock on the Hub.",
    exampleDefault:
      "Example: Office A starts at 09:00 with 15 minutes check-in grace; Office B allows flexible hours and requires GPS on check-in.",
    howDefault:
      "Select an office, adjust schedule and policies, then Save. Changes apply only to that office. Employee edit/delete flags control whether Daily Logs allow direct changes or force correction requests.",
    steps: [
      { icon: "bi-geo-alt", text: "Select the office you are configuring." },
      { icon: "bi-clock", text: "Set workday times, grace, and session policies." },
      { icon: "bi-check2", text: "Save — rules apply to that office only." },
    ],
  },
};

export const AttendanceHelpModal = ({ helpKey, onClose, titleId = "attendance-help-title" }) => {
  const { t } = useTranslation();
  const help = helpKey ? ATTENDANCE_HELP[helpKey] : null;

  useEffect(() => {
    if (!help) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [help, onClose]);

  if (!help) return null;

  return (
    <div
      className="att-help-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="att-help-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="att-help-modal__header">
          <span className="att-help-modal__icon" aria-hidden>
            <i className={`bi ${help.icon}`} />
          </span>
          <div>
            <h2 id={titleId}>
              {t(`app.attendance.help.${helpKey}.title`, { defaultValue: help.titleDefault })}
            </h2>
            <p className="att-help-modal__subtitle">
              {t("app.attendance.help.popupSubtitle", {
                defaultValue: "Quick guide so your team understands this correctly.",
              })}
            </p>
          </div>
          <button
            type="button"
            className="att-help-modal__close"
            onClick={onClose}
            aria-label={t("app.attendance.help.close", { defaultValue: "Close" })}
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div className="att-help-modal__body">
          <div className="att-help-block">
            <h3>{t("app.attendance.help.meaning", { defaultValue: "Meaning" })}</h3>
            <p>{t(`app.attendance.help.${helpKey}.meaning`, { defaultValue: help.meaningDefault })}</p>
          </div>
          <div className="att-help-block">
            <h3>{t("app.attendance.help.whyUseful", { defaultValue: "Why it is useful" })}</h3>
            <p>{t(`app.attendance.help.${helpKey}.why`, { defaultValue: help.whyDefault })}</p>
          </div>
          <div className="att-help-block">
            <h3>{t("app.attendance.help.example", { defaultValue: "Example" })}</h3>
            <p>{t(`app.attendance.help.${helpKey}.example`, { defaultValue: help.exampleDefault })}</p>
          </div>
          <div className="att-help-block">
            <h3>{t("app.attendance.help.howItWorks", { defaultValue: "How it works here" })}</h3>
            <p>{t(`app.attendance.help.${helpKey}.how`, { defaultValue: help.howDefault })}</p>
          </div>

          {Array.isArray(help.steps) && help.steps.length > 0 ? (
            <ol className="att-help-steps">
              {help.steps.map((step) => (
                <li key={step.text}>
                  <i className={`bi ${step.icon}`} aria-hidden />
                  <span>{step.text}</span>
                </li>
              ))}
            </ol>
          ) : null}
        </div>

        <div className="att-help-modal__footer">
          <button type="button" className="att-help-got-it" onClick={onClose}>
            {t("app.attendance.help.gotIt", { defaultValue: "Got it" })}
          </button>
        </div>
      </div>
    </div>
  );
};

export const AttendanceGuideButton = ({ onClick, label }) => {
  const { t } = useTranslation();
  return (
    <button type="button" className="att-guide-btn" onClick={onClick}>
      <i className="bi bi-info-circle" aria-hidden />
      {label || t("app.attendance.help.guideBtn", { defaultValue: "How this works" })}
    </button>
  );
};
